import Vue        from "vue"
import Vuex       from "vuex"
import * as types from './mutation-types'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    uiLoaded: false,
    count: 0,
    completed: false,
    selectedHospital: null,
    hospitals: [],
    labels: {},
    treatments: [],
    hourlyChoices: [],
    privacyPolicyText: null,
    consentsConfig: [],
    stepStatus: [false, false, false, false, false],
    privacyAgreement: {},
    examData: {},
    patientData: {},
    ssnData: {},
    ssnPriorities: [],
    submitStatus: false,
    days: [],
  },

  getters: {
    getCount: (state) => state.count,

    getLabel: (state) => (id) => state.labels[id],

    getHospitals: (state) => state.hospitals,

    getLabels: (state) => state.labels,

    getSelectedHospital: (state) => state.selectedHospital,

    isCompleted: (state) => state.completed,

    isUiLoaded: (state) => state.uiLoaded,

    getTreatments: (state) => state.treatments,

    getHourlyChoices: (state) => state.hourlyChoices,

    isStepCompleted: (state, getters) => (stepNumber) => stepNumber > 0 ? state.stepStatus[stepNumber] && state.stepStatus[stepNumber - 1] : state.stepStatus[stepNumber],

    getExamData: (state) => state.examData,

    getPatientData: (state) => state.patientData,

    getSsnData: (state) => state.ssnData,

    getSsnPriorities: (state) => state.ssnPriorities,

    getPrivacyPolicyText: (state) => state.privacyPolicyText,

    getConsentsConfig: (state) => state.consentsConfig,

    getPrivacyAgreementData: (state) => state.privacyAgreement,

    getSubmitStatus: (state) => state.submitStatus
  },

  mutations: {
    [types.SET_COUNT](state) {
      state.count++
    },

    [types.SET_HOSPITAL](state, selectedHospital) {
      state.selectedHospital = selectedHospital
    },

    [types.SET_COMPLETED](state, value) {
      state.completed = value
    },

    [types.SET_UI_LOADED](state, value) {
      state.uiLoaded = value
    },

    [types.SET_HOSPITALS](state, value) {
      state.hospitals = value
    },

    [types.SET_LABELS](state, value) {
      state.labels = value
    },

    [types.SET_HOURLY_CHOICES](state, value) {
      state.hourlyChoices = value
    },

    [types.SET_TREATMENTS](state, value) {
      state.treatments = value
    },

    [types.SET_STEP_STATUS](state, {index, status}) {
      let stepStatus = [...state.stepStatus];
      stepStatus[index] = status;
      state.stepStatus = stepStatus;
    },

    [types.RESET_STEP_STATUS](state, index) {
      let stepStatus = [...state.stepStatus];
      stepStatus = stepStatus.map((elem, i) => !(i >= index));
      state.stepStatus = stepStatus;
    },

    [types.SET_PRIVACY_AGREEMENT](state, value) {
      state.privacyAgreement = value;
    },

    [types.SET_EXAM_DATA](state, value) {
      state.examData = value;
    },

    [types.SET_PATIENT_DATA](state, value) {
      state.patientData = value;
    },

    [types.SET_SSN_DATA](state, value) {
      state.ssnData = value;
    },

    [types.SET_SSN_PRIORITIES](state, value) {
      state.ssnPriorities = value;
    },

    [types.SET_PRIVACY_POLICY_TEXT](state, value) {
      state.privacyPolicyText = value;
    },

    [types.SET_CONSENTS_CONFIG](state, value) {
      state.consentsConfig = value || [];
    },

    [types.SET_SUBMIT_STATUS](state, value) {
      state.submitStatus = value;
    }
  },

  actions: {
    initForm(context, vm) {

      let bootstrapService = vm.$http.get(`${vm.$config.mgnlServiceEndpoint}?bootstrap`);

      return bootstrapService.then(response => {

        let hospitals = response.data.hospitals;
        let labels = response.data.labels;
        let consentsConfig = response.data.consents;

        context.commit(types.SET_HOSPITALS, hospitals);
        context.commit(types.SET_LABELS, labels);
        context.commit(types.SET_CONSENTS_CONFIG, consentsConfig);
        context.commit(types.SET_UI_LOADED, true);
      }).catch(error => {
        console.error(error.data);
      });

    },

    completeStep(context, index) {
      context.commit(types.SET_STEP_STATUS, {index, status: true})
    },

    resetStepsTo(context, index) {
      context.commit(types.RESET_STEP_STATUS, index);
    },

    setPrivacyAgreement(context, value) {
      context.commit(types.SET_PRIVACY_AGREEMENT, value);
    },

    setExamData(context, value) {
      context.commit(types.SET_EXAM_DATA, value);
    },

    setPatientData(context, patientData) {
      context.commit(types.SET_PATIENT_DATA, patientData);
    },

    setSsnData(context, ssnData) {
      context.commit(types.SET_SSN_DATA, ssnData);
    },

    setHospitalInfo(context, {vm, hospitalId}) {

      let hospitalInfoService = vm.$http.get(`${vm.$config.mgnlServiceEndpoint}?hospitalInfo&hospitalID=${hospitalId}`);

      return hospitalInfoService.then(response => {

        context.commit(types.SET_HOSPITAL, response.data && response.data.hospital);
        context.commit(types.SET_HOURLY_CHOICES, response.data && response.data.hourlyChoices);
        context.commit(types.SET_TREATMENTS, response.data && response.data.treatments);
        context.commit(types.SET_SSN_PRIORITIES, response.data && response.data.ssnPriorities);
        context.commit(types.SET_PRIVACY_POLICY_TEXT, response.data && response.data.privacyPolicyText);

      }).catch(error => {
        console.error(error.data);
      });
    },

    setRegistration(context, vm) {

      let ssnDataDto = {
        ...context.state.examData,
        ...context.state.patientData,
        ...context.state.privacyAgreement,
        ...context.state.ssnData
      };
      ssnDataDto['hospitalUnit'] = context.state.selectedHospital.title;
      ssnDataDto['hospitalUnitID'] = context.state.selectedHospital.id;

      vm.$recaptcha('sendmail').then((token) => {

        ssnDataDto['cupEmail'] = context.state.selectedHospital.mailCup;
        ssnDataDto['captcha'] = token;

        return vm.$http.post(`${vm.$config.mgnlServiceEndpoint}?submitForm`, ssnDataDto).then(response => {
          context.commit(types.SET_SUBMIT_STATUS, true); // in case of success submit status = true will show a thank you message
        }).catch(response => {
          console.error(response)
        }).finally(response => { // in case of error submit status will stay false and a msg error will be showed
          context.commit(types.SET_COMPLETED, true); // in any case the user will be sent to final page
        })
      });

      vm.$config.enableSSN && vm.$config.enableRegistrationService && vm.$recaptcha('registration').then((token) => {
        ssnDataDto['captcha'] = token;
        vm.$http.post(`${vm.$config.mgnlServiceEndpoint}?registerService`, ssnDataDto).then(response => {
          console.log(response.data);
        }).catch(response => {
          console.error(response);
        });
      });

    }
  }


})